import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import GridLabel from './GridLabel';
import HomeProfileIcon from './HomeProfileIcon';
import {
  HOME_PROFILE_QUESTIONS,
  HVAC_SYSTEMS,
  choiceSelected,
  parseHvacSystems,
  toggleHomeChoice,
} from '../shared/homeProfile';

const HVAC_CHOICES = [
  { id: 1, label: '1', description: 'One outdoor unit.' },
  { id: 2, label: '2', description: 'Two outdoor units.' },
  { id: 3, label: '3', description: 'Three outdoor units.' },
  { id: 4, label: '4+', description: 'Four or more. We will remind you for each.' },
];

function dots(count, index, brand) {
  const nodes = [];
  for (let i = 0; i < count; i += 1) {
    const on = i === index;
    nodes.push(
      <span
        key={i}
        style={{
          width: on ? 18 : 8,
          height: 8,
          borderRadius: 99,
          background: on ? brand : '#c5dddc',
          display: 'inline-block',
        }}
      />
    );
  }
  return nodes;
}

function ChoiceCard({ id, label, description, selected, onClick, disabled, brandColor }) {
  const { isDark, colors } = useTheme();
  const titleColor = isDark ? colors.text : '#083838';
  const detailColor = isDark ? colors.textSecondary : '#3f6f6e';
  const iconColor = isDark ? '#59c4c4' : '#0f766e';
  return (
    <button
      type="button"
      data-testid={`home-choice-${id}`}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
        width: '100%',
        textAlign: 'left',
        minHeight: 92,
        padding: '0.8rem 0.9rem',
        borderRadius: '1rem',
        border: selected ? `2px solid ${brandColor}` : (isDark ? '1px solid #4a4a4a' : '1px solid #d5e4e4'),
        background: isDark ? (selected ? '#2c4544' : colors.surfaceAlt) : (selected ? '#e7f7f7' : '#fff'),
        color: titleColor,
        cursor: disabled ? 'default' : 'pointer',
        boxShadow: !isDark && selected ? '0 6px 16px rgba(15, 118, 110, 0.08)' : 'none',
      }}
    >
      <span
        data-choice-icon=""
        style={{
          width: 64,
          height: 64,
          borderRadius: 16,
          background: isDark ? (selected ? '#1f3332' : '#2a2a2a') : (selected ? '#fff' : '#f3fafa'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
        }}
      >
        <HomeProfileIcon id={String(id)} color={iconColor} />
      </span>
      <span style={{ minWidth: 0 }}>
        <GridLabel style={{ display: 'block', fontWeight: 700, fontSize: '1.05rem', color: titleColor }}>{label}</GridLabel>
        <span data-choice-detail="" style={{ display: 'block', marginTop: 2, color: detailColor, fontSize: '0.95rem', lineHeight: 1.35 }}>
          {description}
        </span>
      </span>
    </button>
  );
}

function QuestionBody({ question, value, onChange, readOnly, brandColor, yearUnsure, setYearUnsure }) {
  if (question.year) {
    const year = value.roof_year ? String(value.roof_year) : '';
    return (
      <div>
        <label htmlFor="home-roof-year" style={{ display: 'block', fontWeight: 650, marginBottom: 8 }}>
          Year
        </label>
        <input
          id="home-roof-year"
          data-testid="home-roof-year"
          inputMode="numeric"
          placeholder="2014"
          value={year}
          disabled={readOnly}
          onChange={(event) => {
            const next = event.target.value.replace(/[^\d]/g, '').slice(0, 4);
            setYearUnsure(false);
            onChange(Object.assign({}, value, { roof_year: next ? Number(next) : null }));
          }}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            minHeight: 56,
            fontSize: '1.25rem',
            padding: '0.75rem 1rem',
            borderRadius: '0.9rem',
            border: `2px solid ${brandColor}`,
          }}
        />
        <div style={{ marginTop: 12 }}>
          <ChoiceCard
            id="not_sure"
            label="Not sure"
            description="That is okay. We will use a once-a-year look."
            selected={yearUnsure && !year}
            disabled={readOnly}
            brandColor={brandColor}
            onClick={() => {
              setYearUnsure(true);
              onChange(Object.assign({}, value, { roof_year: null }));
            }}
          />
        </div>
      </div>
    );
  }
  if (question.systems) {
    const listed = Array.isArray(value.hvac_types) && value.hvac_types.length > 0;
    const systems = parseHvacSystems(listed ? value.hvac_types : null, listed ? null : value.hvac_count);
    const setCount = (id, delta) => {
      const next = Object.assign({}, systems);
      const count = Math.max(0, Math.min(8, (next[id] || 0) + delta));
      if (count === 0) delete next[id];
      else next[id] = count;
      const field = Object.keys(next).map((key) => key + ':' + next[key]);
      const ducted = (next.central || 0) + (next.heat_pump || 0);
      onChange(Object.assign({}, value, { hvac_types: field, hvac_count: ducted || null }));
    };
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {HVAC_SYSTEMS.map((system) => (
          <div key={system.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              aria-pressed={(systems[system.id] || 0) > 0}
              disabled={readOnly}
              onClick={() => setCount(system.id, (systems[system.id] || 0) > 0 ? -systems[system.id] : 1)}
              style={{
                flex: '1 1 auto',
                minHeight: 48,
                textAlign: 'left',
                borderRadius: '0.85rem',
                border: (systems[system.id] || 0) > 0 ? `2px solid ${brandColor}` : '1px solid #d5e4e4',
                background: (systems[system.id] || 0) > 0 ? '#e7f7f7' : '#fff',
                color: '#083838',
                padding: '0.6rem 0.8rem',
                cursor: readOnly ? 'default' : 'pointer',
              }}
            >
              <span style={{ display: 'block', fontWeight: 700, color: '#083838' }}>{system.label}</span>
              <span style={{ display: 'block', color: '#3f6f6e', fontSize: '0.95rem' }}>{system.description}</span>
            </button>
            <button type="button" aria-label={`Fewer ${system.label}`} disabled={readOnly || !(systems[system.id])} onClick={() => setCount(system.id, -1)} style={{ minWidth: 44, minHeight: 44 }}>−</button>
            <span aria-label={`${system.label} count`} style={{ minWidth: 16, textAlign: 'center' }}>{systems[system.id] || 0}</span>
            <button type="button" aria-label={`More ${system.label}`} disabled={readOnly} onClick={() => setCount(system.id, 1)} style={{ minWidth: 44, minHeight: 44 }}>+</button>
          </div>
        ))}
      </div>
    );
  }
  if (question.hvac) {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {HVAC_CHOICES.map((choice) => (
          <ChoiceCard
            key={choice.id}
            id={choice.id}
            label={choice.label}
            description={choice.description}
            selected={Number(value.hvac_count || 1) === choice.id || (choice.id === 4 && Number(value.hvac_count) >= 4)}
            disabled={readOnly}
            brandColor={brandColor}
            onClick={() => onChange(Object.assign({}, value, { hvac_count: choice.id }))}
          />
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {question.options.map((option) => (
        <ChoiceCard
          key={option.id}
          id={option.id}
          label={option.label}
          description={option.description}
          selected={choiceSelected(value[question.id], option.id, question.multi, question.listKind)}
          disabled={readOnly}
          brandColor={brandColor}
          onClick={() => {
            if (readOnly) return;
            const next = toggleHomeChoice(value[question.id], option.id, question.multi, question.listKind);
            onChange(Object.assign({}, value, { [question.id]: next }));
          }}
        />
      ))}
    </div>
  );
}

export function HomeFactsInviteCard({ onOpen, onDismiss }) {
  const { colors } = useTheme();
  return (
    <section
      data-testid="home-facts-invite"
      style={{
        background: colors.surface,
        border: '1px solid #b7e0df',
        borderRadius: '1rem',
        padding: '1.1rem 1.15rem',
        marginBottom: '1rem',
      }}
    >
      <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.25rem', color: colors.text }}>
        Finish your home details
      </h2>
      <p style={{ margin: '0 0 0.9rem', color: colors.textSecondary, lineHeight: 1.45 }}>
        Add the walls, roof, air conditioners, and water heater. Home facts keeps them in one place.
      </p>
      <button
        type="button"
        onClick={onOpen}
        style={{
          width: '100%',
          minHeight: 52,
          border: 'none',
          borderRadius: '0.85rem',
          background: '#59c4c4',
          color: '#083838',
          fontWeight: 750,
          fontSize: '1rem',
          cursor: 'pointer',
        }}
      >
        Open Home facts
      </button>
      <button
        type="button"
        onClick={onDismiss}
        style={{
          width: '100%',
          minHeight: 44,
          marginTop: 8,
          border: 'none',
          background: 'transparent',
          color: colors.textSecondary,
          fontSize: '0.95rem',
          cursor: 'pointer',
        }}
      >
        Not now
      </button>
    </section>
  );
}

export function HomeProfilePromptCard({ onStart, onDismiss }) {
  const { colors } = useTheme();
  return (
    <section
      data-testid="home-profile-prompt"
      style={{
        background: colors.surface,
        border: '1px solid #b7e0df',
        borderRadius: '1rem',
        padding: '1.1rem 1.15rem',
        marginBottom: '1rem',
      }}
    >
      <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.25rem', color: colors.text }}>
        Tell us about your home (1 min)
      </h2>
      <p style={{ margin: '0 0 0.9rem', color: colors.textSecondary, lineHeight: 1.45 }}>
        A few taps about the walls, roof, and air conditioners. Then the chore list fits this house.
      </p>
      <button
        type="button"
        onClick={onStart}
        style={{
          width: '100%',
          minHeight: 52,
          border: 'none',
          borderRadius: '0.85rem',
          background: '#59c4c4',
          color: '#083838',
          fontWeight: 750,
          fontSize: '1rem',
          cursor: 'pointer',
        }}
      >
        Tell us about your home
      </button>
      <button
        type="button"
        onClick={onDismiss}
        style={{
          width: '100%',
          minHeight: 44,
          marginTop: 8,
          border: 'none',
          background: 'transparent',
          color: colors.textSecondary,
          fontSize: '0.95rem',
          cursor: 'pointer',
        }}
      >
        Not now
      </button>
    </section>
  );
}

/**
 * The same home questions in setup, the agent form, and Property Details.
 * Wizard shows one question at a time. Editor shows every answer.
 */
export default function HomeProfileQuestions({
  value,
  onChange,
  mode = 'editor',
  onFinish,
  onSkip,
  readOnly = false,
  brandColor = '#59c4c4',
  initialIndex = 0,
}) {
  const { colors } = useTheme();
  const [index, setIndex] = useState(initialIndex);
  const [yearUnsure, setYearUnsure] = useState(false);
  const questions = HOME_PROFILE_QUESTIONS;
  const safeIndex = Math.min(Math.max(index, 0), questions.length - 1);
  const shown = mode === 'wizard' ? [questions[safeIndex]] : questions;
  const legacy = (value && value.warnings) || [];

  return (
    <div data-testid={mode === 'wizard' ? 'home-profile-wizard' : 'home-profile-editor'}>
      {mode === 'wizard' ? (
        <div
          role="status"
          aria-label={`Question ${safeIndex + 1} of ${questions.length}`}
          style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'center' }}
        >
          {dots(questions.length, safeIndex, brandColor)}
        </div>
      ) : null}
      {shown.map((question) => (
        <section key={question.id} data-testid={`home-question-${question.id}`} style={{ marginBottom: mode === 'editor' ? 22 : 0 }}>
          <h3 style={{ margin: '0 0 0.25rem', fontSize: mode === 'wizard' ? '1.45rem' : '1.15rem', color: colors.text, fontWeight: 700 }}>
            {question.title}
          </h3>
          <p style={{ margin: '0 0 0.85rem', color: colors.textSecondary }}>{question.hint}</p>
          <QuestionBody
            question={question}
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            brandColor={brandColor}
            yearUnsure={yearUnsure}
            setYearUnsure={setYearUnsure}
          />
        </section>
      ))}
      {legacy.length ? (
        <p style={{ color: colors.textSecondary, fontSize: '0.92rem' }}>{legacy[0]}</p>
      ) : null}
      {mode === 'wizard' ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={() => {
              if (safeIndex < questions.length - 1) setIndex(safeIndex + 1);
              else if (onFinish) onFinish();
            }}
            style={{
              minHeight: 52,
              border: 'none',
              borderRadius: '0.85rem',
              background: brandColor,
              color: '#083838',
              fontWeight: 750,
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            {safeIndex < questions.length - 1 ? 'Next' : 'Done'}
          </button>
          {safeIndex > 0 ? (
            <button
              type="button"
              onClick={() => setIndex(safeIndex - 1)}
              style={{
                minHeight: 48,
                borderRadius: '0.85rem',
                border: '1px solid #d5e4e4',
                background: 'transparent',
                color: colors.text,
                fontSize: '1rem',
                cursor: 'pointer',
              }}
            >
              Back
            </button>
          ) : null}
          {onSkip ? (
            <button
              type="button"
              onClick={onSkip}
              style={{
                minHeight: 44,
                border: 'none',
                background: 'transparent',
                color: colors.textSecondary,
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              Skip for now
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
